<template>
  <v-card v-if="stripeData.token">
    <v-card-text>
      <v-layout
        row
        wrap>
        <template v-if="paymentsData.length > 0">
          <v-flex>
            <h2>{{ $t('paymentDetails') }}</h2>
            <v-list class="mx-3">
              <template v-for="(item, index) in paymentsData">
                <v-list-tile :key="index">
                  <v-list-tile-content>
                    <v-list-tile-sub-title>{{ item.text | capitalize }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    {{ item.value | capitalize }}
                  </v-list-tile-action>
                </v-list-tile>
              </template>
            </v-list>
          </v-flex>
        </template>

        <template v-if="addressData.length > 0">
          <v-flex>
            <h2>{{ $t('addressDetails') }}</h2>
            <v-list class="mx-3">
              <template v-for="(item, index) in addressData">
                <v-list-tile :key="index">
                  <v-list-tile-content>
                    <v-list-tile-sub-title>{{ item.text | capitalize }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    {{ item.value | capitalize }}
                  </v-list-tile-action>
                </v-list-tile>
              </template>
            </v-list>
          </v-flex>
        </template>
      </v-layout>

      <h2>{{ $t('orderSummary') }}</h2>
      <v-list class="mx-3">
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title>Gewähltes Paket</v-list-tile-title>
            <v-list-tile-sub-title>{{ planExplanation }}</v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            {{ stripeData.paymentPlan.price / 100 | toEuro }}
          </v-list-tile-action>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-content>
            <v-list-tile-title><h3>{{ $t('total') }}</h3></v-list-tile-title>
          </v-list-tile-content>
          <v-list-tile-action>
            <h3>{{ stripeData.paymentPlan.price / 100 | toEuro }}</h3>
          </v-list-tile-action>
        </v-list-tile>
      </v-list>
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        flat
        @click="$emit('prev')"
      >
        {{ $t('prev') }}
      </v-btn>
      <v-btn
        :disabled="isLoading"
        :loading="isLoading"
        color="primary"
        @click="$emit('complete')"
      >
        {{ $t('checkout') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { toEuro, capitalize } from '@/utils/customFormatters'

export default {
  name: 'SubscriptionCheckOut',
  filters: {
    toEuro,
    capitalize
  },
  props: ['stripeData', 'isLoading'],
  data () {
    return {
      paymentsList: {
        object: {
          text: this.$t('paymentType'),
          value: source => source['object'].split('_').join(' ')
        },
        brand: { text: this.$t('cardType') },
        name: { text: this.$t('name') },
        last4: {
          text: this.$t('last4'),
          value: source => source['last4']
            ? `XXXX XXXX XXXX ${source['last4']}` : null
        },
        exp_year: {
          text: this.$t('expireDate'),
          value: source => source['exp_year'] && source['exp_month']
            ? `${source['exp_month']}`.padStart(2, '0') + '/' + source['exp_year'] : null
        },
        account_holder_name: { text: this.$t('accountHolderName') },
        account_holder_type: { text: this.$t('accountHolderType') },
        currency: {
          text: this.$t('currency'),
          value: source => source['currency'].toUpperCase()
        }
      },
      addressList: {
        address_city: { text: this.$t('address_city') },
        address_line1: { text: this.$t('address_line1') },
        address_line2: { text: this.$t('address_line2') },
        address_state: { text: this.$t('address_state') },
        address_zip: { text: this.$t('address_zip') },
        address_country: { text: this.$t('address_country') }
      }
    }
  },
  computed: {
    planExplanation () {
      const employees = this.stripeData.paymentPlan.max_employees
      return this.$t('planExplanation', { employees })
    },
    paymentsData () {
      return this.detailsFactory(this.paymentsList)
    },
    addressData () {
      return this.detailsFactory(this.addressList)
    }
  },
  methods: {
    detailsFactory (detailsList) {
      const token = this.stripeData.token
      const dataSource = token[token.type]
      return Object.keys(detailsList).reduce((acc, detailKey) => {
        if (dataSource[detailKey]) {
          acc.push({
            text: detailsList[detailKey].text,
            value: detailsList[detailKey].value ? detailsList[detailKey].value(dataSource) : dataSource[detailKey]
          })
        }
        return acc
      }, [])
    }
  },
  i18n: {
    messages: {
      de: {
        paymentDetails: 'Zahlungsübersicht',
        addressDetails: 'Rechnungsadresse',
        address_line1: 'Straße',
        address_line2: 'Addresszusatz',
        address_city: 'Stadt',
        address_state: 'Bundesland',
        address_zip: 'Postleitzahl',
        address_country: 'Land',
        paymentType: 'Zahlungsart',
        cardType: 'Kartentyp',
        expireDate: 'Ablaufdatum',
        last4: 'Kartennummer',
        orderSummary: 'Zusammenfassung',
        total: 'Preis pro Monat',
        planExplanation: 'Max. {employees} Mitarbeiter',
        prev: 'zurück',
        checkout: 'Zahlungspflichtig kaufen',
        accountHolderName: 'Kontoinhaber',
        accountHolderType: 'Kontotyp',
        currency: 'Währung'
      },
      en: {
        paymentDetails: 'Payment overview',
        addressDetails: 'Invoice address',
        address_line1: 'Street',
        address_line2: 'c/o',
        address_city: 'City',
        address_state: 'State',
        address_zip: 'Zip',
        address_country: 'Country',
        paymentType: 'Payment type',
        cardType: 'Card type',
        expireDate: 'Expires',
        last4: 'Card number',
        orderSummary: 'Summary',
        total: 'Price per month',
        planExplanation: 'Max. {employees} employees',
        prev: 'back',
        checkout: 'Commit to Buy',
        accountHolderName: 'Account owner',
        accountHolderType: 'Account',
        currency: 'Currency'
      },
      tr: {
        paymentDetails: 'Ödeme bilgileri',
        addressDetails: 'Fatura adresi',
        address_line1: 'Sokak',
        address_line2: 'Ek adres satırı',
        address_city: 'Şehir',
        address_state: 'Bölge/Eyalet',
        address_zip: 'Posta kodu',
        address_country: 'Ülke',
        paymentType: 'Ödeme türü',
        cardType: 'Kart tipi',
        expireDate: 'Bitiş tarihi',
        last4: 'Kart numarası',
        orderSummary: 'Özet',
        total: 'Aylık ücret',
        planExplanation: 'Maks. {employees} çalışan',
        prev: 'geri',
        checkout: 'Ödeme onayı',
        accountHolderName: 'Hesap sahibi',
        accountHolderType: 'Hesap tipi',
        currency: 'para birimi'
      },
      ru: {
        paymentDetails: 'Обзор платежа',
        addressDetails: 'Адрес выставления счета',
        address_line1: 'Адрес',
        address_line2: 'Адрес 2',
        address_city: 'Город',
        address_state: 'Штат',
        address_zip: 'ZIP-код',
        address_country: 'Страна',
        paymentType: 'Тип оплаты',
        cardType: 'Тип карты',
        expireDate: 'Истекает',
        last4: 'Номер карты',
        orderSummary: 'Итого',
        total: 'Цена за месяц',
        planExplanation: 'Макс. {employees} сотрудников',
        prev: 'Назад',
        checkout: 'Оформить заказ',
        accountHolderName: 'Владелец счета',
        accountHolderType: 'Счет',
        currency: 'Валюта'
      }
    }
  }
}
</script>
